import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Popover } from 'antd'
import {
  FlaskConical,
  ChevronUp,
  ChevronDown,
  ClipboardList,
  Mic,
  ArrowRight,
  History,
} from 'lucide-react'
import {
  ResearchSplitWrapper,
  HistoryToggleButton,
  HistoryCountBadge,
  HistoryPopoverContainer,
  HistoryHeader,
  HistoryHeaderTitle,
  HistoryHeaderCount,
  HistoryList,
  HistoryItem,
  HistoryItemIcon,
  HistoryItemBody,
  HistoryItemLabel,
  HistoryItemMeta,
  HistoryItemArrow,
  HistoryFooter,
  HistoryFooterButton,
  HistoryFooterArrow,
  EmptyHistory,
} from '../../styles/MindMapHeaderStyles'
import { StartResearchRoundButton } from './StartResearchRoundButton'
import { formatRelativeShort } from '../../../../../utils/formatRelativeTime'
import type { ResearchSummaryItem } from '../hooks/useResearchSummary'

const MAX_RECENT_ITEMS = 3

interface ResearchSplitButtonProps {
  mindmapId: string | undefined
  items: ResearchSummaryItem[]
  count: number
  /** False when the mindmap has nothing beyond the root venture node — disables the Start CTA. */
  hasValidatableContent: boolean
  onStartResearch: () => void
}

export const ResearchSplitButton: React.FC<ResearchSplitButtonProps> = ({
  mindmapId,
  items,
  count,
  hasValidatableContent,
  onStartResearch,
}) => {
  const navigate = useNavigate()
  const [historyOpen, setHistoryOpen] = useState(false)

  const recentItems = useMemo(() => items.slice(0, MAX_RECENT_ITEMS), [items])
  const startDisabled = !hasValidatableContent

  const handleItemClick = useCallback(
    (item: ResearchSummaryItem) => {
      setHistoryOpen(false)
      const path =
        item.type === 'survey' ? `/survey/${item.id}/insights` : `/interview/${item.id}/insights`
      void navigate(path)
    },
    [navigate]
  )

  const handleViewAll = useCallback(() => {
    if (!mindmapId) return
    setHistoryOpen(false)
    void navigate(`/mindmap/${mindmapId}/research`)
  }, [mindmapId, navigate])

  const popoverContent = (
    <HistoryPopoverContainer>
      <HistoryHeader>
        <HistoryHeaderTitle>Research history</HistoryHeaderTitle>
        <HistoryHeaderCount>
          {count} round{count !== 1 ? 's' : ''}
        </HistoryHeaderCount>
      </HistoryHeader>

      {recentItems.length === 0 ? (
        <EmptyHistory>
          {hasValidatableContent
            ? 'No research done yet — start a round to gather real-world feedback on your ideas.'
            : 'Add some unmet needs to your mindmap first, then come back here to start validating them.'}
        </EmptyHistory>
      ) : (
        <HistoryList>
          {recentItems.map((item) => {
            const meta =
              item.type === 'survey'
                ? `${item.respondentCount} response${item.respondentCount !== 1 ? 's' : ''}`
                : `${item.respondentCount} interview${item.respondentCount !== 1 ? 's' : ''}`
            return (
              <HistoryItem
                key={`${item.type}-${item.id}`}
                type="button"
                onClick={() => handleItemClick(item)}
              >
                <HistoryItemIcon $type={item.type}>
                  {item.type === 'survey' ? <ClipboardList size={16} /> : <Mic size={16} />}
                </HistoryItemIcon>
                <HistoryItemBody>
                  <HistoryItemLabel>
                    {item.label ||
                      (item.type === 'survey' ? 'Untitled survey' : 'Untitled interview')}
                  </HistoryItemLabel>
                  <HistoryItemMeta>
                    {meta} · {formatRelativeShort(item.createdAt)}
                  </HistoryItemMeta>
                </HistoryItemBody>
                <HistoryItemArrow>
                  <ArrowRight size={14} />
                </HistoryItemArrow>
              </HistoryItem>
            )
          })}
        </HistoryList>
      )}

      {count > 0 && (
        <HistoryFooter>
          <HistoryFooterButton type="button" onClick={handleViewAll} disabled={!mindmapId}>
            <History size={13} />
            <span>View all research</span>
            <HistoryFooterArrow>
              <ArrowRight size={13} />
            </HistoryFooterArrow>
          </HistoryFooterButton>
        </HistoryFooter>
      )}
    </HistoryPopoverContainer>
  )

  return (
    <ResearchSplitWrapper $disabled={startDisabled}>
      <StartResearchRoundButton
        enabled={hasValidatableContent}
        attached
        onClick={onStartResearch}
      />
      <Popover
        content={popoverContent}
        trigger="click"
        placement="bottomRight"
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        styles={{ body: { padding: 0, background: 'transparent', boxShadow: 'none' } }}
      >
        <HistoryToggleButton
          type="button"
          $open={historyOpen}
          $muted={startDisabled}
          aria-label="Research history"
        >
          <FlaskConical size={16} />
          <HistoryCountBadge $muted={startDisabled}>{count}</HistoryCountBadge>
          {historyOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </HistoryToggleButton>
      </Popover>
    </ResearchSplitWrapper>
  )
}
